import { Alert, Input, InputNumber, Modal, Select, Typography } from 'antd';
import { type ReactNode } from 'react';

type RuleRow = {
  key: string;
  id: string;
  name: string;
  event: string;
  order: number;
  actionId: string;
  actionType: string;
  actionInputsJson: string;
  conditionsJson: string;
};

type CreateWorkflowActionInputs = {
  workflow_template_id?: string;
  workflow_template_override_id?: string;
  workflow_additional_inputs?: Record<string, unknown>;
  due_date_days?: number;
  assignment_strategy?: 'round_robin' | 'random' | string;
  assignment_roles?: string[];
  priority?: string;
};

type Option = {
  value: string;
  label: string;
};

type RuleEditorModalProps = {
  open: boolean;
  editingRule: RuleRow | null;
  onClose: () => void;
  onUpdateRuleRow: (key: string, patch: Partial<RuleRow>) => void;
  ruleActionTypeOptions: Option[];
  currentActionInputs: CreateWorkflowActionInputs;
  setCurrentActionInputField: (
    field: keyof CreateWorkflowActionInputs,
    value: unknown
  ) => void;
  templateOptions: Option[];
  templateOverrideOptions: Option[];
  priorityOptions: Option[];
  actionInputsError?: string | null;
  conditionsError?: string | null;
  additionalInputsDraft: string;
  setAdditionalInputsDraft: (value: string) => void;
  additionalInputsError?: string | null;
  parseJson: (raw: string) => {
    value: Record<string, unknown> | null;
    error: string | null;
  };
};

const renderFieldLabel = (label: string, required = false) => (
  <span>
    {label}
    {required ? <span className="text-[#ff4d4f] ml-1">*</span> : null}
  </span>
);

const HorizontalField = ({
  label,
  children,
  help,
}: {
  label: ReactNode;
  children: ReactNode;
  help?: ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <div className="w-56 pt-2">
      <Typography.Text type="secondary">{label}</Typography.Text>
    </div>
    <div className="flex-1">
      {children}
      {help ? <div className="mt-1">{help}</div> : null}
    </div>
  </div>
);

export const RuleEditorModal = ({
  open,
  editingRule,
  onClose,
  onUpdateRuleRow,
  ruleActionTypeOptions,
  currentActionInputs,
  setCurrentActionInputField,
  templateOptions,
  templateOverrideOptions,
  priorityOptions,
  actionInputsError,
  conditionsError,
  additionalInputsDraft,
  setAdditionalInputsDraft,
  additionalInputsError,
  parseJson,
}: RuleEditorModalProps) => {
  return (
    <Modal
      title={`Rule Details${editingRule?.id ? ` - ${editingRule.id}` : ''}`}
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText="Done"
      width={880}
      destroyOnClose
    >
      {editingRule ? (
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <Typography.Title level={5} className="!mb-3">
              Rule
            </Typography.Title>
            <div className="space-y-3">
              <HorizontalField label={renderFieldLabel('Rule ID', true)}>
                <Input
                  value={editingRule.id}
                  onChange={(event) =>
                    onUpdateRuleRow(editingRule.key, { id: event.target.value })
                  }
                  placeholder="rule_id"
                />
              </HorizontalField>

              <HorizontalField label={renderFieldLabel('Name', true)}>
                <Input
                  value={editingRule.name}
                  onChange={(event) =>
                    onUpdateRuleRow(editingRule.key, {
                      name: event.target.value,
                    })
                  }
                  placeholder="Rule name"
                />
              </HorizontalField>

              <HorizontalField label={renderFieldLabel('Event', true)}>
                <Input
                  value={editingRule.event}
                  onChange={(event) =>
                    onUpdateRuleRow(editingRule.key, {
                      event: event.target.value,
                    })
                  }
                  placeholder="service_request.created"
                />
              </HorizontalField>

              <HorizontalField label={renderFieldLabel('Order', true)}>
                <InputNumber
                  className="w-full"
                  value={editingRule.order}
                  onChange={(value) =>
                    onUpdateRuleRow(editingRule.key, {
                      order: Number(value || 1),
                    })
                  }
                  min={1}
                  precision={0}
                />
              </HorizontalField>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <Typography.Title level={5} className="!mb-3">
              Action
            </Typography.Title>
            <div className="space-y-3">
              <HorizontalField label={renderFieldLabel('Action ID', true)}>
                <Input
                  value={editingRule.actionId}
                  onChange={(event) =>
                    onUpdateRuleRow(editingRule.key, {
                      actionId: event.target.value,
                    })
                  }
                  placeholder="action_1"
                />
              </HorizontalField>

              <HorizontalField label={renderFieldLabel('Action Type', true)}>
                {ruleActionTypeOptions.length ? (
                  <Select
                    className="w-full"
                    value={editingRule.actionType || undefined}
                    onChange={(value) =>
                      onUpdateRuleRow(editingRule.key, {
                        actionType: value || '',
                      })
                    }
                    options={ruleActionTypeOptions}
                    showSearch
                    allowClear
                    placeholder="Select action type"
                  />
                ) : (
                  <Input
                    value={editingRule.actionType}
                    onChange={(event) =>
                      onUpdateRuleRow(editingRule.key, {
                        actionType: event.target.value,
                      })
                    }
                    placeholder="create_workflow"
                  />
                )}
              </HorizontalField>

              {editingRule.actionType === 'create_workflow' ? (
                <>
                  <HorizontalField label={renderFieldLabel('Template', true)}>
                    <Select
                      className="w-full"
                      value={currentActionInputs.workflow_template_id}
                      options={templateOptions}
                      showSearch
                      allowClear
                      placeholder="Select workflow template"
                      onChange={(value) => {
                        setCurrentActionInputField(
                          'workflow_template_id',
                          value
                        );
                        if (value) {
                          setCurrentActionInputField(
                            'workflow_template_override_id',
                            undefined
                          );
                        }
                      }}
                    />
                    <Typography.Text type="secondary" className="mt-2 block">
                      OR
                    </Typography.Text>
                  </HorizontalField>

                  <HorizontalField
                    label={renderFieldLabel('Template Override', true)}
                  >
                    <Select
                      className="w-full"
                      value={currentActionInputs.workflow_template_override_id}
                      options={templateOverrideOptions}
                      showSearch
                      allowClear
                      placeholder="Select workflow template override"
                      onChange={(value) => {
                        setCurrentActionInputField(
                          'workflow_template_override_id',
                          value
                        );
                        if (value) {
                          setCurrentActionInputField(
                            'workflow_template_id',
                            undefined
                          );
                        }
                      }}
                    />
                  </HorizontalField>

                  <HorizontalField label={renderFieldLabel('Priority', true)}>
                    <Select
                      className="w-full"
                      value={currentActionInputs.priority}
                      options={priorityOptions}
                      showSearch
                      allowClear
                      placeholder="Select priority"
                      onChange={(value) =>
                        setCurrentActionInputField('priority', value)
                      }
                    />
                  </HorizontalField>

                  <HorizontalField label="Assignment Strategy">
                    <Select
                      className="w-full"
                      value={currentActionInputs.assignment_strategy}
                      options={[
                        { value: 'round_robin', label: 'Round Robin' },
                        { value: 'random', label: 'Random' },
                      ]}
                      allowClear
                      placeholder="Select assignment strategy"
                      onChange={(value) =>
                        setCurrentActionInputField('assignment_strategy', value)
                      }
                    />
                  </HorizontalField>

                  <HorizontalField label="Due Date (days)">
                    <InputNumber
                      className="w-full"
                      min={0}
                      precision={0}
                      value={currentActionInputs.due_date_days}
                      onChange={(value) =>
                        setCurrentActionInputField(
                          'due_date_days',
                          value == null ? undefined : Number(value)
                        )
                      }
                    />
                  </HorizontalField>

                  <HorizontalField label="Assignment Roles">
                    <Select
                      mode="tags"
                      className="w-full"
                      value={currentActionInputs.assignment_roles || []}
                      placeholder="Enter Frontegg role names"
                      onChange={(value) =>
                        setCurrentActionInputField('assignment_roles', value)
                      }
                    />
                  </HorizontalField>

                  <HorizontalField
                    label="Additional Inputs JSON"
                    help={
                      additionalInputsError ? (
                        <Alert
                          type="error"
                          showIcon
                          message={`Invalid JSON: ${additionalInputsError}`}
                        />
                      ) : undefined
                    }
                  >
                    <Input.TextArea
                      className="font-mono"
                      spellCheck={false}
                      value={additionalInputsDraft}
                      onChange={(event) => {
                        const raw = event.target.value;
                        setAdditionalInputsDraft(raw);
                        const parsed = parseJson(raw || '{}');
                        if (!parsed.error && parsed.value) {
                          setCurrentActionInputField(
                            'workflow_additional_inputs',
                            parsed.value
                          );
                        }
                      }}
                      autoSize={{ minRows: 6, maxRows: 12 }}
                      placeholder='{"from_number":"+1234567890"}'
                    />
                  </HorizontalField>
                </>
              ) : (
                <HorizontalField
                  label="Action Inputs JSON"
                  help={
                    actionInputsError ? (
                      <Alert
                        type="error"
                        showIcon
                        message={`Invalid JSON: ${actionInputsError}`}
                      />
                    ) : undefined
                  }
                >
                  <Input.TextArea
                    className="font-mono"
                    spellCheck={false}
                    value={editingRule.actionInputsJson}
                    onChange={(event) =>
                      onUpdateRuleRow(editingRule.key, {
                        actionInputsJson: event.target.value,
                      })
                    }
                    autoSize={{ minRows: 8, maxRows: 16 }}
                    placeholder='{"workflow_template_id":"..."}'
                  />
                </HorizontalField>
              )}
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <Typography.Title level={5} className="!mb-3">
              Condition
            </Typography.Title>
            <HorizontalField
              label="Conditions JSON (optional)"
              help={
                conditionsError ? (
                  <Alert
                    type="error"
                    showIcon
                    message={`Invalid JSON: ${conditionsError}`}
                  />
                ) : undefined
              }
            >
              <Input.TextArea
                className="font-mono"
                spellCheck={false}
                value={editingRule.conditionsJson}
                onChange={(event) =>
                  onUpdateRuleRow(editingRule.key, {
                    conditionsJson: event.target.value,
                  })
                }
                autoSize={{ minRows: 8, maxRows: 16 }}
                placeholder='{"all_of":[{"field":"...","operator":"eq","value":"..."}]}'
              />
            </HorizontalField>
          </div>
        </div>
      ) : (
        <Alert type="warning" showIcon message="Rule not found." />
      )}
    </Modal>
  );
};
